import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { List } from "../../pages/List/schema/list";

import { listApi } from "./list.api";
import { BulkFindPersonPlan, listFindPersonApi } from "./list-find-person.api";

/** A plan as one opening of the bulk Find people dialog holds it. */
export type FindPeoplePlanState =
	| { status: "loading" }
	| { status: "ready"; plan: BulkFindPersonPlan }
	| { status: "error" };

export interface ListFindPersonState {
	/**
	 * The full list per id, as the dialog reads its search settings. Bucketed by the id the list
	 * was requested for: RTK Query serves a warm cache entry without re-dispatching `fulfilled`, so
	 * a single bucket would show whichever list was fetched last (apps/web CLAUDE.md §2). The list
	 * slice's `currentFullList` is that single bucket.
	 */
	listById: Record<number, List>;
	/**
	 * Each opening's plan, by the opening's key: an answer that lands after the dialog was closed
	 * and opened again for another selection is filed under the opening that asked for it.
	 */
	planBySession: Record<string, FindPeoplePlanState>;
}

const initialState: ListFindPersonState = { listById: {}, planBySession: {} };

export const listFindPersonSlice = createSlice({
	name: "listFindPerson",
	initialState,
	reducers: {
		/** Drops an opening's plan when its dialog closes. */
		forgetFindPeoplePlan: (state, { payload }: PayloadAction<string>) => {
			delete state.planBySession[payload];
		},
	},
	extraReducers: (builder) => {
		builder.addMatcher(listApi.endpoints.getList.matchFulfilled, (state, { payload, meta }) => {
			state.listById[meta.arg.originalArgs] = payload;
		});
		builder.addMatcher(
			listFindPersonApi.endpoints.planBulkFindPerson.matchPending,
			(state, { meta }) => {
				state.planBySession[meta.arg.originalArgs.sessionKey] = { status: "loading" };
			}
		);
		// An answer for an opening that was closed meanwhile (its plan forgotten) is dropped.
		builder.addMatcher(
			listFindPersonApi.endpoints.planBulkFindPerson.matchFulfilled,
			(state, { payload, meta }) => {
				const { sessionKey } = meta.arg.originalArgs;

				if (state.planBySession[sessionKey]) {
					state.planBySession[sessionKey] = { status: "ready", plan: payload };
				}
			}
		);
		builder.addMatcher(
			listFindPersonApi.endpoints.planBulkFindPerson.matchRejected,
			(state, { meta }) => {
				const { sessionKey } = meta.arg.originalArgs;

				if (state.planBySession[sessionKey]) {
					state.planBySession[sessionKey] = { status: "error" };
				}
			}
		);
	},
});

export const { forgetFindPeoplePlan } = listFindPersonSlice.actions;
export const listFindPersonReducer = listFindPersonSlice.reducer;

/** The full list the dialog was opened on, once read. */
export const findPeopleListSelector =
	(listId: number) =>
	(state: RootState): List | undefined =>
		state.listFindPerson.listById[listId];

/** An opening's plan; one not asked for yet reads as loading. */
export const findPeoplePlanSelector =
	(sessionKey: string) =>
	(state: RootState): FindPeoplePlanState =>
		state.listFindPerson.planBySession[sessionKey] ?? { status: "loading" };
